import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { BaseDialogComponent } from '@src/app/modules/base';
import { AlertService } from '@src/core/services';

import { ApiService } from '../../services';
import { ChatMultimediaType } from '../types';
import { AvatarService } from '../../components/photo/services';

import { FormControlsType, FormDataType, InputDataType } from './types';

@Component({
  selector: 'app-chat-edit-form',
  templateUrl: './chat-edit-form.component.html',
  styleUrls: ['./chat-edit-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatEditFormComponent extends BaseDialogComponent<InputDataType, boolean> {
  private readonly api = inject(ApiService);
  private readonly alertService = inject(AlertService);
  private readonly translateService = inject(TranslateService);
  private readonly avatarService = inject(AvatarService);

  loading = false;
  newPhoto?: ChatMultimediaType;

  form = new FormGroup<FormControlsType>({
    chatName: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    chatDescription: new FormControl(''),
    onlyForAdmins: new FormControl(false, { nonNullable: true }),
  });

  ngOnInit(): void {
    super.ngOnInit();

    if (this.data) {
      const formData = this.mapInputDataToFormData(this.data);
      this.form.patchValue(formData);
    }
  }

  onPhotoChange(newPhoto?: ChatMultimediaType): void {
    this.newPhoto = newPhoto;
    this.cdr.markForCheck();
  }

  override cancel() {
    this.newPhoto = undefined;
    return super.cancel(false);
  }

  override apply() {
    this.form.markAllAsTouched();

    if (this.form.invalid || !this.data?.chatId) {
      this.alertService.error(this.translateService.instant('common.alerts.errors.fillAndCheckFields'));
      return;
    }

    this.api.editChat(this.data.chatId, this.form.getRawValue()).subscribe(() => {
      if (this.newPhoto && this.data) {
        this.avatarService.editChatAvatar(this.data.chatId, this.newPhoto.file).subscribe();
      }

      this.newPhoto = undefined;
      return super.apply(true);
    });
  }

  private mapInputDataToFormData(data: InputDataType) {
    const { chatName, chatDescription, onlyForAdmins } = data;
    const formData: FormDataType = {
      chatName,
      chatDescription,
      onlyForAdmins,
    };
    return formData;
  }
}
